import React from 'react';
import {
  BirdzDialog,
  BirdzModalTitle,
  BirdzNotif,
  useDialog,
  useNotif
} from '@applications-terrains/birdz-react-library';
import { ParamsConfigType } from '../../../Admin/Params/Types';
import { useEffect, useState } from 'react';
import ModalWithLoader, { RequestStatus } from '../../../Tools/ModalWithLoader/ModalWithLoader';
import { displayClientsOrContracts, translate } from './utils';
import { useTranslation } from 'react-i18next';
import { Box, Button, IconButton, Typography } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import ContentCopy from '@mui/icons-material/ContentCopy';
import { Edit } from '@mui/icons-material';
import axios from 'axios';
import BTable from '../../../Tools/Table/BTable';
import TrueFalse from '../../../Tools/TrueFalse';
import { AddCircle } from '@mui/icons-material';
import { ExocetFamilyType, ExocetRadioModeType } from '../Types';
import ConfigurationForm from './ConfigurationForm';
import { ConfigurationAction, DefaultRadioConfig, RadioConfig } from './types';
import useRefresh from '../../../../hooks/useRefresh';
import { Client } from '../../../DndComponents/ContractsDnd/types';

const ConfigurationsTable = ({
  config,
  filterString,
  moduleFamilies,
  radioModes,
  setCreateDefaultConfig,
  setEditDefaultConfig,
  defaultConfigurations,
  triggerRefreshFromParent,
  clients = []
}: {
  config: ParamsConfigType;
  filterString: string;
  moduleFamilies?: ExocetFamilyType[];
  radioModes?: ExocetRadioModeType[];
  setCreateDefaultConfig: (value: RadioConfig | null) => void;
  setEditDefaultConfig: (value: DefaultRadioConfig | null) => void;
  defaultConfigurations: DefaultRadioConfig[];
  triggerRefreshFromParent: () => void;
  clients?: Client[];
}) => {
  const [action, setAction] = useState<ConfigurationAction>(null);
  const [selectedConfig, setSelectedConfig] = useState<RadioConfig | null>(null);
  const [requestStatus, setRequestStatus] = useState<RequestStatus>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const { refresh: refreshConfigs, triggerRefresh } = useRefresh();
  const { confirmDialog, closeDialog, dialogOptions } = useDialog();
  const { notif, notifOptions } = useNotif();
  const associatedDefaultConfig =
    defaultConfigurations.find((defaultConfig) => defaultConfig.profile === selectedConfig?.id) ??
    null;

  // Les questions posées après un enregistrement conservent le libellé de l'action
  // qui les a produites : la modale de succès et son en-tête restent cohérents.
  const isAskingToCreateDefault =
    action === 'askingForDefault' || action === 'askingForDefaultAfterEdit';
  const isAskingToEditDefault = action === 'askingToEditDefault';
  const isEditFlow =
    action === 'edit' || action === 'askingToEditDefault' || action === 'askingForDefaultAfterEdit';

  const handleCloseModalWithLoader = () => {
    setAction(null);
    setRequestStatus(null);
    setSelectedConfig(null);
    setErrorMessage(null);
  };

  useEffect(() => {
    if (requestStatus === 'success' && action === 'edit') {
      triggerRefresh();
      setSelectedConfig(null);
    }
  }, [requestStatus, triggerRefresh, action]);

  const { t } = useTranslation();

  const refresh = () => {
    triggerRefresh();
    setTimeout(() => {
      triggerRefreshFromParent();
    }, 0);
  };

  const actions = [
    {
      name: 'duplicate',
      label: t('equipmentConfiguration.duplicateRadio'),
      render: (item: RadioConfig) => {
        return (
          <IconButton
            onClick={() => {
              setAction('duplicate');
              setSelectedConfig(item);
            }}
          >
            <ContentCopy fontSize="small" />
          </IconButton>
        );
      }
    },
    {
      name: 'edit',
      label: t('equipmentConfiguration.editRadio'),
      render: (item: RadioConfig) => {
        return (
          <IconButton
            onClick={() => {
              setAction('edit');
              setSelectedConfig(item);
            }}
          >
            <Edit fontSize="small" />
          </IconButton>
        );
      }
    },
    {
      name: 'delete',
      label: t('equipmentConfiguration.deleteRadio'),
      render: (param: RadioConfig) => {
        return (
          <IconButton
            onClick={() => {
              const defaultConfig = defaultConfigurations.find(
                (config) => config.profile === param.id
              );
              confirmDialog({
                title: t('equipmentConfiguration.deleteRadioTitle'),
                content: defaultConfig
                  ? ` ${t('equipmentConfiguration.deleteRadioDefaultConfigWarning')}`
                  : t('equipmentConfiguration.deleteRadioContent'),
                onValidate: async () => {
                  setAction('delete');
                  try {
                    const requests = [];
                    if (defaultConfig) {
                      requests.push(
                        axios.delete(`/api/boi/params/default-configurations/${defaultConfig.id}/`)
                      );
                    }
                    requests.push(
                      axios.delete(`/api/boi/params/configuration-profiles/${param.id}/`)
                    );
                    await Promise.all(requests);
                    refresh();
                    notif({
                      content: t(
                        `equipmentConfiguration.${defaultConfig ? 'deleteConfigurationsSuccess' : 'deleteRadioSuccess'}`
                      ),
                      type: 'success'
                    });
                  } catch (error) {
                    notif({
                      content: t('equipmentConfiguration.deleteError'),
                      type: 'error'
                    });
                  } finally {
                    setAction(null);
                    closeDialog();
                  }
                },
                onCancel: () => {
                  closeDialog();
                }
              });
            }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        );
      }
    }
  ];

  return (
    <>
      <Typography variant="overline" ml={4} mt={2}>
        {t('equipmentConfiguration.configurations')}
      </Typography>
      <BTable
        model="equipment-configurations"
        endpoint={config?.endpoint + filterString.replace('&', '?')}
        defaultOrder={'id'}
        fields={[
          // {
          //   name: 'id',
          //   label: 'ID',
          //   type: 'number'
          // },
          {
            name: 'name',
            label: t('equipmentConfiguration.name'),
            type: 'text'
          },
          {
            name: 'families',
            label: t('equipmentConfiguration.equipmentFamilies'),
            type: 'select-multiple',
            transform: (values) => {
              const familiesName: string[] = [];
              if (values?.length === moduleFamilies?.length) {
                return t('equipmentConfiguration.allFamilies');
              } else
                moduleFamilies?.map((family: any) => {
                  values?.forEach((value: number) => {
                    if (value === family.Id) {
                      const name = translate(family.Name);
                      familiesName.push(name);
                    }
                  });
                });
              return familiesName.join(', ');
            }
          },
          {
            name: 'radio_mode',
            label: t('equipmentConfiguration.radioMode'),
            transform: (value) => {
              const radioMode =
                radioModes?.find((mode: ExocetRadioModeType) => value === mode.Id)?.Label ?? value;
              return radioMode;
            }
          },
          {
            name: 'dataset_code',
            label: t('equipmentConfiguration.datasetCodeLabel'),
            type: 'text'
          },
          {
            name: 'dst',
            label: 'DST',
            type: 'text',
            transform: (value: boolean) => {
              return <TrueFalse value={value} />;
            }
          },
          {
            name: 'hourly_index',
            label: t('equipmentConfiguration.hourly_index'),
            type: 'text',
            transform: (value: boolean) => {
              return <TrueFalse value={value} />;
            }
          },
          {
            name: 'autoconnect',
            label: 'Autoconnect',
            type: 'text',
            transform: (value: boolean) => {
              return <TrueFalse value={value} />;
            }
          },
          {
            name: 'clients_and_contracts',
            label: `${t('equipmentConfiguration.clients')} / ${t('equipmentConfiguration.contracts')}`,
            transform: (_, row: RadioConfig) => {
              return (
                <Box display={'flex'} flexDirection={'row'} gap={2}>
                  {row.authorised_customers.length === 0 &&
                  row.authorised_contracts.length === 0 &&
                  row.excluded_customers.length === 0 &&
                  row.excluded_contracts.length === 0 ? (
                    t('equipmentConfiguration.all')
                  ) : (
                    <>
                      {(row.authorised_customers.length > 0 ||
                        row.authorised_contracts.length > 0) && (
                        <Box width={'200px'} display={'flex'} flexDirection={'column'} gap={1}>
                          {row.authorised_customers.length > 0 && (
                            <Box display={'flex'} flexDirection={'column'}>
                              <Box
                                fontSize={'body2.fontSize'}
                                fontWeight={'500'}
                                color={'rgba(0, 0, 0, 0.6)'}
                              >
                                {t('equipmentConfiguration.authorizedClients')}
                              </Box>
                              {displayClientsOrContracts(row.authorised_customers, clients)}
                            </Box>
                          )}
                          {row.authorised_contracts.length > 0 && (
                            <Box display={'flex'} flexDirection={'column'}>
                              <Box
                                fontSize={'body2.fontSize'}
                                fontWeight={'500'}
                                color={'rgba(0, 0, 0, 0.6)'}
                              >
                                {t('equipmentConfiguration.authorizedContracts')}
                              </Box>
                              {displayClientsOrContracts(row.authorised_contracts, clients)}
                            </Box>
                          )}
                        </Box>
                      )}
                      {(row.excluded_customers.length > 0 || row.excluded_contracts.length > 0) && (
                        <Box width={'200px'} display={'flex'} flexDirection={'column'} gap={1}>
                          {row.excluded_customers.length > 0 && (
                            <Box display={'flex'} flexDirection={'column'}>
                              <Box
                                fontSize={'body2.fontSize'}
                                fontWeight={'500'}
                                color={'rgba(0, 0, 0, 0.6)'}
                              >
                                {t('equipmentConfiguration.excludedClients')}
                              </Box>
                              {displayClientsOrContracts(row.excluded_customers, clients)}
                            </Box>
                          )}
                          {row.excluded_contracts.length > 0 && (
                            <Box display={'flex'} flexDirection={'column'}>
                              <Box
                                fontSize={'body2.fontSize'}
                                fontWeight={'500'}
                                color={'rgba(0, 0, 0, 0.6)'}
                              >
                                {t('equipmentConfiguration.excludedContracts')}
                              </Box>
                              {displayClientsOrContracts(row.excluded_contracts, clients)}
                            </Box>
                          )}
                        </Box>
                      )}
                    </>
                  )}
                </Box>
              );
            }
          }
        ]}
        actions={actions}
        refresh={refreshConfigs}
        headerActions={[
          {
            name: t('equipmentConfiguration.addConfiguration'),
            element: (
              <IconButton onClick={() => setAction('add')}>
                <AddCircle fontSize="small" color="action" />
              </IconButton>
            )
          }
        ]}
      />
      <BirdzDialog options={dialogOptions} />
      <BirdzNotif options={notifOptions} />
      <ModalWithLoader
        customErrorMessage={errorMessage}
        customSuccessMessage={
          (isAskingToCreateDefault || isAskingToEditDefault) && (
            <Box className="d-flex flex-column align-items-center" mt={2}>
              {isAskingToEditDefault
                ? t('equipmentConfiguration.askToEditDefaultConfig')
                : t('equipmentConfiguration.askForDefaultConfig')}
              <Box display={'flex'} gap={2} mt={2}>
                <Button
                  variant="outlined"
                  color="error"
                  onClick={() => {
                    handleCloseModalWithLoader();
                    refresh();
                  }}
                >
                  {t('no')}
                </Button>
                <Button
                  variant="outlined"
                  color="info"
                  onClick={() => {
                    if (isAskingToEditDefault) {
                      setEditDefaultConfig(associatedDefaultConfig);
                    } else {
                      setCreateDefaultConfig(selectedConfig);
                    }
                    handleCloseModalWithLoader();
                  }}
                >
                  {t('yes')}
                </Button>
              </Box>
            </Box>
          )
        }
        openModal={
          action === 'add' ||
          action === 'edit' ||
          action === 'duplicate' ||
          isAskingToCreateDefault ||
          isAskingToEditDefault
        }
        onClose={() => handleCloseModalWithLoader()}
        onSuccess={() => handleCloseModalWithLoader()}
        action={
          action === 'delete'
            ? t('equipmentConfiguration.deleteRadioAction')
            : isEditFlow
              ? t('equipmentConfiguration.editRadioAction')
              : t('equipmentConfiguration.createRadioAction')
        }
        status={requestStatus}
        setStatus={setRequestStatus}
        maxWidth="xl"
        header={
          <BirdzModalTitle>
            {isEditFlow
              ? t('bstock.configurationDetail.editTitle')
              : t('bstock.configurationDetail.addTitle')}
          </BirdzModalTitle>
        }
      >
        <ConfigurationForm
          setRequestStatus={setRequestStatus}
          action={action}
          setAction={(value, config) => {
            setAction(value);
            value === null && setSelectedConfig(null);
            config && setSelectedConfig(config);
            if (value === 'askingForDefault') {
              refresh();
            }
          }}
          config={selectedConfig}
          moduleFamilies={moduleFamilies}
          clients={clients}
          radioModes={radioModes}
          setErrorMessage={setErrorMessage}
          triggerRefresh={refresh}
          associatedDefaultConfig={associatedDefaultConfig}
          notif={notif}
        />
      </ModalWithLoader>
    </>
  );
};

export default ConfigurationsTable;
